import { useState, useEffect, useCallback } from 'react';
import { ServiceExchange, CreateExchangeData } from '@/types';
import {
  createExchange,
  getUserExchanges,
  getIncomingRequests,
  getScheduledExchanges,
  acceptExchange,
  declineExchange,
  cancelExchange,
  completeExchange,
} from '@/services';

export interface UseExchangesReturn {
  exchanges: ServiceExchange[];
  incomingRequests: ServiceExchange[];
  scheduledExchanges: ServiceExchange[];
  loading: boolean;
  error: string | null;
  requestService: (data: CreateExchangeData) => Promise<string>;
  acceptRequest: (exchangeId: string) => Promise<void>;
  declineRequest: (exchangeId: string) => Promise<void>;
  cancelService: (exchangeId: string, reason?: string) => Promise<void>;
  completeService: (exchangeId: string) => Promise<void>;
  refreshExchanges: () => Promise<void>;
}

/**
 * Custom hook for service exchanges
 */
export const useExchanges = (userId?: string): UseExchangesReturn => {
  const [exchanges, setExchanges] = useState<ServiceExchange[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<ServiceExchange[]>([]);
  const [scheduledExchanges, setScheduledExchanges] = useState<ServiceExchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadExchanges = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [all, incoming, scheduled] = await Promise.all([
        getUserExchanges(userId),
        getIncomingRequests(userId),
        getScheduledExchanges(userId),
      ]);

      setExchanges(all);
      setIncomingRequests(incoming);
      setScheduledExchanges(scheduled);
    } catch (err) {
      setError('Failed to load exchanges');
      console.error('Error loading exchanges:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadExchanges();
  }, [loadExchanges]);

  const requestService = async (data: CreateExchangeData): Promise<string> => {
    try {
      setError(null);
      const exchangeId = await createExchange(data);
      await loadExchanges();
      return exchangeId;
    } catch (err) {
      setError('Failed to request service');
      console.error('Error requesting service:', err);
      throw err;
    }
  };

  const acceptRequest = async (exchangeId: string): Promise<void> => {
    try {
      setError(null);
      await acceptExchange(exchangeId);
      await loadExchanges();
    } catch (err) {
      setError('Failed to accept request');
      console.error('Error accepting request:', err);
      throw err;
    }
  };

  const declineRequest = async (exchangeId: string): Promise<void> => {
    try {
      setError(null);
      await declineExchange(exchangeId);
      await loadExchanges();
    } catch (err) {
      setError('Failed to decline request');
      console.error('Error declining request:', err);
      throw err;
    }
  };

  const cancelService = async (exchangeId: string, reason?: string): Promise<void> => {
    if (!userId) return;

    try {
      setError(null);
      await cancelExchange(exchangeId, userId, reason);
      await loadExchanges();
    } catch (err) {
      setError('Failed to cancel service');
      console.error('Error cancelling service:', err);
      throw err;
    }
  };

  const completeService = async (exchangeId: string): Promise<void> => {
    try {
      setError(null);
      await completeExchange(exchangeId);
      await loadExchanges();
    } catch (err) {
      setError('Failed to complete service');
      console.error('Error completing service:', err);
      throw err;
    }
  };

  const refreshExchanges = async (): Promise<void> => {
    await loadExchanges();
  };

  return {
    exchanges,
    incomingRequests,
    scheduledExchanges,
    loading,
    error,
    requestService,
    acceptRequest,
    declineRequest,
    cancelService,
    completeService,
    refreshExchanges,
  };
};
